import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useSocket } from '../../contexts/SocketContext';
import MessageBubble from './MessageBubble';
import MessageComposer from './MessageComposer';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { ArrowLeft, DotsThreeVertical, Headset, Archive } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../contexts/AuthContext';
// Imported (not /public) so the build content-hashes the filename — a new
// image gets a new URL, so CDN/browser caches can never serve a stale one.
import chatBg from '../../assets/bg/private-support.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * `inShell` is the member's view inside the 2.0 shell: the page around it
 * already says Private Support and draws the artwork, so the header here is
 * reduced to the case's state and its menu, and the safe areas belong to the
 * shell. Staff see the full header until their surfaces are rebuilt.
 */
const ChatWindow = ({
  chat, onBack, onToggleDisappearing,
  inShell = false, staffInShell = false, capsuleAutoOpen = false, onCapsuleCancel,
}) => {
  // Either way the shell owns the safe areas and draws the artwork; only the
  // member's view reduces the header.
  const framed = inShell || staffInShell;
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';
  const [localMessages, setLocalMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { messages, joinChat, leaveChat, typingUsers, setMessages: setSocketMessages } = useSocket();
  const messagesEndRef = useRef(null);

  // Held locally as well as on the chat, so pressing close shows immediately
  // rather than waiting for the parent to refetch.
  const [closure, setClosure] = useState({
    closedAt: chat?.closed_at || null,
    purgeAt: chat?.purge_at || null,
  });

  useEffect(() => {
    setClosure({ closedAt: chat?.closed_at || null, purgeAt: chat?.purge_at || null });
  }, [chat?.id, chat?.closed_at, chat?.purge_at]);

  const closeCase = async () => {
    if (!window.confirm(
      'Close this case?\n\n'
      + 'Everything in it is removed three days from now. '
      + 'Either of you can reopen it before then, and writing again reopens it too.'
    )) return;
    try {
      const { data } = await axios.post(
        `${API_URL}/api/chats/${chat.id}/close`, {}, { withCredentials: true },
      );
      setClosure({ closedAt: data.closed_at, purgeAt: data.purge_at });
    } catch (e) { /* the banner simply does not appear */ }
  };

  const reopenCase = async () => {
    try {
      await axios.post(`${API_URL}/api/chats/${chat.id}/reopen`, {}, { withCredentials: true });
      setClosure({ closedAt: null, purgeAt: null });
    } catch (e) { /* ignore */ }
  };

  const loadMessages = useCallback(async (chatId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chats/${chatId}/messages`, {
        withCredentials: true,
      });
      setLocalMessages(data);
      setSocketMessages([]);
      // Opening the thread marks it read for this user (clears the badge).
      axios.post(`${API_URL}/api/chats/${chatId}/read`, {}, { withCredentials: true })
        .catch(() => {});
    } catch (error) {
      // Silent fail - UI shows empty state
    } finally {
      setLoading(false);
    }
  }, [setSocketMessages]);

  useEffect(() => {
    if (!chat) return;
    loadMessages(chat.id);
    joinChat(chat.id);
    return () => {
      leaveChat(chat.id);
    };
  }, [chat, loadMessages, joinChat, leaveChat]);

  useEffect(() => {
    const chatMessages = messages.filter(m => m.chat_id === chat?.id);
    if (chatMessages.length === 0) return;
    let addedInbound = false;
    setLocalMessages(prev => {
      const existingIds = new Set(prev.map(m => m.id));
      const newMessages = chatMessages.filter(m => !existingIds.has(m.id));
      addedInbound = newMessages.some(m => m.sender_id !== user?.id);
      return newMessages.length > 0 ? [...prev, ...newMessages] : prev;
    });
    // A message arrived while the user is looking at this thread → keep the
    // server-side read marker current so it doesn't resurface as unread.
    if (addedInbound && chat?.id) {
      axios.post(`${API_URL}/api/chats/${chat.id}/read`, {}, { withCredentials: true })
        .catch(() => {});
    }
  }, [messages, chat, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localMessages]);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const typingText = Object.values(typingUsers).filter(Boolean).join(', ');

  if (!chat) {
    return (
      <div className={`flex-1 flex items-center justify-center${framed ? '' : ' bg-background'}`}>
        <div className="text-center">
          <Headset className="w-16 h-16 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">Select a support thread</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col min-h-0${framed ? '' : ' bg-background'}`} data-testid="chat-window">
      {/* Header */}
      <div className={`sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border px-4 ${inShell ? 'py-1.5' : framed ? 'py-3' : 'py-3 safe-top'}`}>
        <div className="flex items-center gap-3">
          {inShell ? (
            // The member's one conversation: what matters is whether the case
            // is open, not whose name is on it (it is their own).
            <p className="flex-1 text-xs text-muted-foreground" data-testid="case-status">
              {closure.closedAt ? 'Case closed' : 'Case open'}
              {chat.disappearing_messages_enabled ? ' · disappearing messages' : ''}
            </p>
          ) : (<>
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="md:hidden"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>

          {/* The number, as in the queue. This is the header of an open
              thread, so it sits on screen for as long as the conversation
              lasts -- which makes it the one most worth not being a name. */}
          <Avatar className="w-10 h-10 border border-border">
            <AvatarFallback className="bg-muted text-muted-foreground text-sm tabular-nums">
              {chat.member_no != null
                ? String(chat.member_no).padStart(3, '0')
                : getInitials(chat.name)}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <h2 className="font-heading font-medium text-foreground truncate">
              {chat.name || 'Support'}
            </h2>
            <p className="text-xs text-muted-foreground">
              Support thread
              {chat.disappearing_messages_enabled ? ' · disappearing' : ''}
            </p>
          </div>
          </>)}

          {/* The menu is no longer staff-only: closing a case is something a
              member may do to their own conversation. Only the disappearing
              setting stays with the Founder. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="touch-icon" aria-label="Case options" data-testid="chat-options">
                <DotsThreeVertical className="w-5 h-5" weight="bold" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {closure.closedAt ? (
                <DropdownMenuItem onClick={reopenCase} data-testid="reopen-case">
                  Reopen this case
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={closeCase} data-testid="close-case">
                  Close this case
                </DropdownMenuItem>
              )}
              {isSuperadmin && (
                <DropdownMenuItem
                  onClick={() => onToggleDisappearing(chat)}
                  data-testid="toggle-disappearing"
                >
                  {chat.disappearing_messages_enabled ? 'Disable' : 'Enable'} disappearing messages
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Said plainly and once, at the top, rather than repeated under every
            message — a warning shown on every row stops being read. */}
        {closure.purgeAt && (
          <div
            className="flex items-start gap-2 mt-3 text-xs rounded-lg border border-border bg-muted/50 px-3 py-2"
            data-testid="case-closed-banner"
          >
            <Archive className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
            <span className="text-muted-foreground">
              This case is closed. Everything in it is removed{' '}
              <strong className="text-foreground">
                {(() => {
                  try {
                    return formatDistanceToNow(new Date(closure.purgeAt), { addSuffix: true });
                  } catch (e) { return 'in three days'; }
                })()}
              </strong>
              . Writing again reopens it.
            </span>
          </div>
        )}
      </div>

      {/* Messages */}
      <div
        className="flex-1 min-h-0 overflow-y-auto p-4"
        data-testid="messages-container"
        // In the shell the page already carries the artwork and its scrim.
        style={framed ? undefined : {
          backgroundImage:
            `linear-gradient(rgba(7,9,10,0.34), rgba(7,9,10,0.34)), url(${chatBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">Loading messages...</p>
          </div>
        ) : localMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          localMessages.map(message => (
            <MessageBubble key={message.id} message={message} />
          ))
        )}
        
        {typingText && (
          <div className="text-sm text-muted-foreground italic px-4">
            {typingText} {typingText.includes(',') ? 'are' : 'is'} typing...
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Composer */}
      <MessageComposer
        chatId={chat.id}
        inShell={framed}
        capsuleAutoOpen={capsuleAutoOpen}
        onCapsuleCancel={onCapsuleCancel}
      />
    </div>
  );
};

export default ChatWindow;
